import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { track as analyticsTrack } from "@/lib/analytics";

const SettingsContext = createContext({ settings: null, loading: true, refresh: () => {} });

// Google Fonts specs for the CMS typography picker
export const GOOGLE_FONTS = {
  "Fraunces": "Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700",
  "Playfair Display": "Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500",
  "Cormorant Garamond": "Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400",
  "DM Serif Display": "DM+Serif+Display:ital@0;1",
  "Marcellus": "Marcellus",
  "EB Garamond": "EB+Garamond:ital,wght@0,400;0,500;0,600;1,400",
  "Libre Caslon Text": "Libre+Caslon+Text:ital,wght@0,400;0,700;1,400",
  "Lora": "Lora:ital,wght@0,400;0,500;0,600;1,400",
  "Manrope": "Manrope:wght@300;400;500;600;700;800",
  "Inter": "Inter:ital,wght@0,300;0,400;0,500;0,600;0,700",
  "Jost": "Jost:ital,wght@0,300;0,400;0,500;0,600;1,400",
  "Work Sans": "Work+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400",
  "Nunito Sans": "Nunito+Sans:ital,opsz,wght@0,6..12,300;0,6..12,400;0,6..12,600;0,6..12,700;1,6..12,400",
  "Karla": "Karla:ital,wght@0,300;0,400;0,500;0,600;1,400",
  "Source Sans 3": "Source+Sans+3:ital,wght@0,300;0,400;0,500;0,600;1,400",
};

// Applies CMS typography settings as CSS variables + loads any needed Google Fonts
export const applyTypography = (ty = {}) => {
  const root = document.documentElement;
  const setVar = (k, v) => (v ? root.style.setProperty(k, v) : root.style.removeProperty(k));
  setVar("--font-display", ty.heading_font ? `'${ty.heading_font}'` : "");
  setVar("--font-body", ty.body_font ? `'${ty.body_font}'` : "");
  setVar("--hr-body-size", ty.body_size ? `${ty.body_size}px` : "");
  setVar("--hr-line-height", ty.line_height ? String(ty.line_height) : "");
  setVar("--hr-para-gap", ty.paragraph_gap ? `${ty.paragraph_gap}rem` : "");
  setVar("--hr-headline-gap", ty.headline_gap !== undefined && ty.headline_gap !== "" && ty.headline_gap !== null ? `${ty.headline_gap}em` : "");
  setVar("--hr-heading-tracking", ty.heading_tracking !== undefined && ty.heading_tracking !== "" && ty.heading_tracking !== null ? `${ty.heading_tracking}em` : "");
  setVar("--hr-heading-weight", ty.heading_weight ? String(ty.heading_weight) : "");
  const fams = [...new Set([ty.heading_font, ty.body_font].filter((f) => f && GOOGLE_FONTS[f] && f !== "Fraunces" && f !== "Manrope"))];
  if (fams.length) {
    const href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${GOOGLE_FONTS[f]}`).join("&")}&display=swap`;
    let link = document.getElementById("hr-dynamic-fonts");
    if (!link) {
      link = document.createElement("link");
      link.id = "hr-dynamic-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== href) link.setAttribute("href", href);
  }
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [s, navPages] = await Promise.all([
        api.get("/public/settings"),
        api.get("/public/nav-pages").catch(() => ({ data: [] })),
      ]);
      const merged = { ...(s.data || {}) };
      const existingUrls = new Set((merged.header_menu || []).flatMap((m) => [m.url, ...(m.children || []).map((c) => c.url)]).filter(Boolean));
      const extra = (navPages.data || []).filter((x) => x && x.url && x.label && !existingUrls.has(x.url));
      if (extra.length) {
        merged.header_menu = [...(merged.header_menu || []), ...extra];
      }
      setSettings(merged);
    } catch (e) {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Apply typography whenever settings change
  useEffect(() => {
    if (settings) applyTypography(settings.typography || {});
  }, [settings]);

  // Apply favicon + apple-touch-icon from settings
  useEffect(() => {
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    if (settings?.favicon_url) setLink("icon", settings.favicon_url);
    if (settings?.apple_icon_url) setLink("apple-touch-icon", settings.apple_icon_url);
  }, [settings?.favicon_url, settings?.apple_icon_url]);

  // Inject tracking scripts whenever settings change
  useEffect(() => {
    if (!settings?.tracking) return;
    const t = settings.tracking;
    const cleanups = [];

    const addScript = (src, inner, attrs = {}) => {
      const s = document.createElement("script");
      if (src) s.src = src;
      if (inner) s.innerHTML = inner;
      Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
      s.async = true;
      s.dataset.hrTracking = "1";
      document.head.appendChild(s);
      cleanups.push(() => s.remove());
    };

    // GA4
    if (t.ga4_id) {
      addScript(`https://www.googletagmanager.com/gtag/js?id=${t.ga4_id}`, null);
      addScript(null, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config','${t.ga4_id}');`);
    }
    // GTM
    if (t.gtm_id) {
      addScript(null, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${t.gtm_id}');`);
    }
    // Meta Pixel
    if (t.meta_pixel_id) {
      addScript(null, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${t.meta_pixel_id}');fbq('track','PageView');`);
    }
    // Google Ads
    if (t.google_ads_id) {
      addScript(`https://www.googletagmanager.com/gtag/js?id=${t.google_ads_id}`);
      addScript(null, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('config','${t.google_ads_id}');`);
    }
    // Custom scripts
    if (t.custom_header) addScript(null, t.custom_header);
    if (t.custom_footer) addScript(null, t.custom_footer);

    // Search console / FB domain verify meta tags
    const addMeta = (name, content) => {
      const m = document.createElement("meta");
      m.name = name; m.content = content;
      m.dataset.hrTracking = "1";
      document.head.appendChild(m);
      cleanups.push(() => m.remove());
    };
    if (t.search_console_verify) addMeta("google-site-verification", t.search_console_verify);
    if (t.fb_domain_verify) addMeta("facebook-domain-verification", t.fb_domain_verify);

    return () => cleanups.forEach((c) => c());
  }, [settings]);

  // Optional: inject the Emergent "Made with Emergent" badge script if the admin has re-enabled it
  useEffect(() => {
    const EMERGENT_SRC = "https://assets.emergent.sh/scripts/emergent-main.js";
    const existing = document.querySelector(`script[data-hr-emergent="1"]`);
    if (settings?.show_emergent_badge) {
      if (!existing) {
        const s = document.createElement("script");
        s.src = EMERGENT_SRC;
        s.async = true;
        s.dataset.hrEmergent = "1";
        document.head.appendChild(s);
      }
    } else if (existing) {
      existing.remove();
      // Best-effort cleanup of any badge DOM injected by the script
      document.querySelectorAll('[id*="emergent"], [class*="emergent-badge"], [class*="madewith"], a[href*="emergent.sh"]').forEach((el) => {
        if (el.closest("#root")) return; // don't touch app content
        el.remove();
      });
    }
  }, [settings?.show_emergent_badge]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

// Helper: fire a tracking event across GA4, Ads, Meta + first-party analytics
export const trackEvent = (eventName, params = {}) => {
  try {
    if (window.gtag) window.gtag("event", eventName, params);
    if (window.fbq) window.fbq("trackCustom", eventName, params);
    if (window.dataLayer) window.dataLayer.push({ event: eventName, ...params });
    analyticsTrack("event", { event: eventName, params });
  } catch (e) { /* ignore */ }
};

// Read UTMs + click ids from current URL/document
export const collectAttribution = () => {
  if (typeof window === "undefined") return {};
  const url = new URL(window.location.href);
  const p = url.searchParams;
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_content: p.get("utm_content") || "",
    utm_term: p.get("utm_term") || "",
    gclid: p.get("gclid") || "",
    fbclid: p.get("fbclid") || "",
    referrer: document.referrer || "",
    source_page_url: window.location.href,
  };
};

export const useSettings = () => useContext(SettingsContext);
